import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Tag, Loader2, ArrowLeft, Plus, Copy, Power, PowerOff, Trash2, X, Search,
  Package, Store as StoreIcon, Calendar, Users, Link2, Eye, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

// -------- URL builder --------
const buildPromoUrl = (row) => {
  if (row.kind === 'product' && row.album_id) {
    return `${SITE_ORIGIN}/release/${row.album_id}?p=${row.token}`;
  }
  return `${SITE_ORIGIN}/?p=${row.token}`;
};

const humanStatus = (row) => {
  if (!row.enabled) return { label: 'Disabled', tone: 'text-[#71717A] bg-[#27272A]' };
  if (row.expires_at && new Date(row.expires_at) <= new Date()) return { label: 'Expired', tone: 'text-yellow-400 bg-yellow-400/10 border border-yellow-400/30' };
  if (row.max_clicks && row.click_count >= row.max_clicks) return { label: 'Click-capped', tone: 'text-orange-400 bg-orange-400/10 border border-orange-400/30' };
  if (row.total_redemption_limit && row.redemption_count >= row.total_redemption_limit) return { label: 'Fully redeemed', tone: 'text-purple-400 bg-purple-400/10 border border-purple-400/30' };
  return { label: 'Active', tone: 'text-[#00F0FF] bg-[#00F0FF]/10 border border-[#00F0FF]/30' };
};

// -------- Product picker with instant search --------
const ProductPicker = ({ albums, value, onChange }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return albums;
    const rx = q.trim().toLowerCase();
    return albums.filter((a) => (a.title || '').toLowerCase().includes(rx) || (a.artist || '').toLowerCase().includes(rx));
  }, [albums, q]);
  const selected = albums.find((a) => a.id === value);
  return (
    <div>
      {selected ? (
        <div className="flex items-center gap-3 p-3 bg-[#0F0F11] border border-[#00F0FF]/30 rounded-md" data-testid="product-picker-selected">
          {selected.cover_url && <img src={selected.cover_url} alt="" className="w-12 h-12 rounded object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{selected.title}</div>
            <div className="text-xs text-[#71717A] truncate">{selected.artist}</div>
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-[#71717A] hover:text-white"
            data-testid="product-picker-clear"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search releases by title or artist…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-[#0F0F11] border border-[#27272A] rounded-md focus:border-[#00F0FF] focus:outline-none"
              data-testid="product-picker-search"
            />
          </div>
          <div className="mt-2 max-h-56 overflow-y-auto border border-[#27272A] rounded-md bg-[#0F0F11]">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-[#71717A]">No releases match "{q}"</div>
            ) : (
              filtered.slice(0, 100).map((a) => (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => onChange(a.id)}
                  className="w-full flex items-center gap-3 p-2 hover:bg-[#1A1A1D] text-left"
                  data-testid={`product-picker-option-${a.id}`}
                >
                  {a.cover_url && <img src={a.cover_url} alt="" className="w-9 h-9 rounded object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.title}</div>
                    <div className="text-xs text-[#71717A] truncate">{a.artist} · ₱{a.price}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// -------- Create form --------
const CreateForm = ({ albums, onCreated, onCancel }) => {
  const [kind, setKind] = useState('product');
  const [albumId, setAlbumId] = useState('');
  const [percent, setPercent] = useState(15);
  const [minPurchase, setMinPurchase] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [maxUsesPerCustomer, setMaxUsesPerCustomer] = useState(1);
  const [totalLimit, setTotalLimit] = useState('');
  const [maxClicks, setMaxClicks] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (kind === 'product' && !albumId) {
      toast.error('Pick a release for the product discount');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        kind,
        percent: Number(percent),
        max_uses_per_customer: Number(maxUsesPerCustomer) || 1,
        enabled,
        note: note.trim() || null,
      };
      if (kind === 'product') body.album_id = albumId;
      if (kind === 'storewide' && minPurchase !== '') body.min_purchase_php = Number(minPurchase);
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      if (totalLimit) body.total_redemption_limit = Number(totalLimit);
      if (maxClicks) body.max_clicks = Number(maxClicks);
      const { data } = await axios.post(`${API}/admin/discount-urls`, body);
      toast.success('Discount URL created');
      onCreated(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || 'Failed to create discount URL');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#121214] border border-[#27272A] rounded-lg p-5 md:p-6 space-y-5" data-testid="discount-create-form">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold">Create discount URL</h3>
        <button type="button" onClick={onCancel} className="text-[#71717A] hover:text-white" data-testid="discount-create-cancel">
          <X size={18} />
        </button>
      </div>

      {/* Kind switcher */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { v: 'product', label: 'Product-specific', icon: Package },
          { v: 'storewide', label: 'Storewide', icon: StoreIcon },
        ].map((o) => {
          const Icon = o.icon;
          const active = kind === o.v;
          return (
            <button
              type="button"
              key={o.v}
              onClick={() => setKind(o.v)}
              className={`p-3 rounded-md border text-left transition-colors ${
                active ? 'border-[#00F0FF] bg-[#00F0FF]/10' : 'border-[#27272A] hover:border-[#3F3F46]'
              }`}
              data-testid={`discount-kind-${o.v}`}
            >
              <Icon size={16} className={active ? 'text-[#00F0FF]' : 'text-[#71717A]'} />
              <div className={`text-sm mt-1 ${active ? 'text-[#00F0FF]' : ''}`}>{o.label}</div>
            </button>
          );
        })}
      </div>

      {kind === 'product' && (
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Release</label>
          <ProductPicker albums={albums} value={albumId} onChange={setAlbumId} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Discount %</label>
          <input
            type="number" min={1} max={95} step="1" required
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
            data-testid="discount-percent"
          />
        </div>
        {kind === 'storewide' && (
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Min purchase ₱</label>
            <input
              type="number" min={0} step="1"
              value={minPurchase}
              placeholder="e.g. 2000 (leave blank = any cart)"
              onChange={(e) => setMinPurchase(e.target.value)}
              className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
              data-testid="discount-min-purchase"
            />
          </div>
        )}
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Expires (local time)</label>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
            data-testid="discount-expires"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Uses per customer</label>
          <input
            type="number" min={1} step="1"
            value={maxUsesPerCustomer}
            onChange={(e) => setMaxUsesPerCustomer(e.target.value)}
            className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
            data-testid="discount-per-customer"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Total redemption cap</label>
          <input
            type="number" min={1} step="1"
            value={totalLimit}
            placeholder="unlimited"
            onChange={(e) => setTotalLimit(e.target.value)}
            className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
            data-testid="discount-total-limit"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Max URL clicks</label>
          <input
            type="number" min={1} step="1"
            value={maxClicks}
            placeholder="unlimited"
            onChange={(e) => setMaxClicks(e.target.value)}
            className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
            data-testid="discount-max-clicks"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">Internal note (only you see this)</label>
        <input
          type="text" maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. IG Story — Volume 9 launch"
          className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm"
          data-testid="discount-note"
        />
      </div>

      <div className="flex items-center gap-3 pt-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="accent-[#00F0FF]"
            data-testid="discount-enabled"
          />
          <span className="text-sm">Enable immediately after creation</span>
        </label>
        <div className="flex-1" />
        <button type="button" onClick={onCancel} className="text-sm px-4 py-2 rounded-md text-[#A1A1AA] hover:text-white">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="text-sm px-5 py-2 rounded-full bg-[#00F0FF] text-black font-semibold hover:bg-[#00E0EE] disabled:opacity-60 flex items-center gap-2"
          data-testid="discount-submit"
        >
          {submitting ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
          Generate URL
        </button>
      </div>
    </form>
  );
};

// -------- Main page --------
const AdminDiscountUrlsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [list, alb] = await Promise.all([
        axios.get(`${API}/admin/discount-urls`),
        axios.get(`${API}/albums`),
      ]);
      setRows(list.data?.discount_urls || []);
      setAlbums(Array.isArray(alb.data) ? alb.data : []);
    } catch {
      toast.error('Failed to load discount URLs');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const rx = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (!rx) return true;
      return (
        (r.token || '').toLowerCase().includes(rx) ||
        (r.note || '').toLowerCase().includes(rx) ||
        (r.album_title || '').toLowerCase().includes(rx)
      );
    });
  }, [rows, q, kindFilter]);

  const copy = (row) => {
    const url = buildPromoUrl(row);
    navigator.clipboard.writeText(url).then(() => toast.success('URL copied — post it anywhere'));
  };

  const toggle = async (row) => {
    try {
      const { data } = await axios.put(`${API}/admin/discount-urls/${row.id}`, { enabled: !row.enabled });
      setRows(rows.map((r) => (r.id === row.id ? { ...r, ...data } : r)));
      toast.success(row.enabled ? 'Disabled' : 'Enabled');
    } catch {
      toast.error('Toggle failed');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${row.kind} promo (${row.token})? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/discount-urls/${row.id}`);
      setRows(rows.filter((r) => r.id !== row.id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10">
        <button
          onClick={() => navigate('/admin/home')}
          className="text-xs text-[#71717A] hover:text-[#00F0FF] inline-flex items-center gap-1 mb-2"
          data-testid="back-to-admin-home"
        >
          <ArrowLeft size={12} /> Admin home
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
            <Tag className="text-[#00F0FF]" size={26} /> Discount URLs
          </h1>
          <button
            onClick={() => setShowCreate((s) => !s)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00F0FF] text-black font-semibold text-sm hover:bg-[#00E0EE]"
            data-testid="new-discount-url-button"
          >
            <Plus size={14} /> New URL
          </button>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-6 max-w-2xl">
          Generate promo links that look like a normal share URL. The buyer sees the regular price on the release page — the discount only shows on the Maya checkout screen. Perfect for IG stories, DMs and closed-DM promos.
        </p>

        {showCreate && (
          <div className="mb-6">
            <CreateForm
              albums={albums}
              onCreated={(row) => { setRows([row, ...rows]); setShowCreate(false); }}
              onCancel={() => setShowCreate(false)}
            />
          </div>
        )}

        {/* Filters */}
        <div className="mb-4 flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by token, note, or release title…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-[#121214] border border-[#27272A] rounded-md focus:border-[#00F0FF] focus:outline-none"
              data-testid="discount-search"
            />
          </div>
          {[
            { v: 'all', label: 'All' },
            { v: 'product', label: 'Product' },
            { v: 'storewide', label: 'Storewide' },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setKindFilter(f.v)}
              className={`px-3 py-1.5 text-xs rounded-full border ${
                kindFilter === f.v ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'
              }`}
              data-testid={`discount-filter-${f.v}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#71717A]"><Loader2 className="mx-auto animate-spin mb-3" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-[#121214] border border-[#27272A] rounded-lg text-sm text-[#71717A]" data-testid="discount-empty">
            {rows.length === 0 ? "No discount URLs yet. Hit \u201cNew URL\u201d to generate your first one." : "No results for the current filter."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filtered.map((row) => {
              const status = humanStatus(row);
              const url = buildPromoUrl(row);
              const remaining =
                row.total_redemption_limit
                  ? Math.max(0, row.total_redemption_limit - (row.redemption_count || 0))
                  : null;
              return (
                <div
                  key={row.id}
                  className="bg-[#121214] border border-[#27272A] rounded-lg p-4 md:p-5 hover:border-[#3F3F46] transition-colors"
                  data-testid={`discount-row-${row.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${row.kind === 'storewide' ? 'bg-purple-500/10 text-purple-300 border border-purple-500/30' : 'bg-blue-500/10 text-blue-300 border border-blue-500/30'}`}>
                          {row.kind}
                        </span>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${status.tone}`} data-testid={`discount-status-${row.id}`}>{status.label}</span>
                        <span className="text-lg font-heading font-semibold text-white">{row.percent}%</span>
                        {row.kind === 'storewide' && row.min_purchase_php ? (
                          <span className="text-xs text-[#A1A1AA]">min ₱{Number(row.min_purchase_php).toLocaleString()}</span>
                        ) : null}
                      </div>
                      <div className="text-sm text-white truncate" data-testid={`discount-target-${row.id}`}>
                        {row.kind === 'product'
                          ? (row.album_title || 'Product') + ' · ' + (row.token)
                          : `Storewide · ${row.token}`}
                      </div>
                      {row.note && (
                        <div className="text-xs text-[#71717A] mt-1 italic">"{row.note}"</div>
                      )}
                      <div className="mt-3 flex items-center gap-2 bg-[#0F0F11] border border-[#27272A] rounded px-3 py-2 text-[11px] text-[#D4D4D8] font-mono break-all">
                        <Link2 size={12} className="text-[#71717A] shrink-0" />
                        <span className="truncate">{url}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#A1A1AA]">
                        <span className="inline-flex items-center gap-1"><Eye size={12} /> {row.click_count || 0} clicks{row.max_clicks ? ` / ${row.max_clicks}` : ''}</span>
                        <span className="inline-flex items-center gap-1"><TrendingUp size={12} /> {row.redemption_count || 0} redemptions{row.total_redemption_limit ? ` / ${row.total_redemption_limit}` : ''}</span>
                        {remaining !== null && (
                          <span className="text-[#00F0FF]">{remaining} remaining</span>
                        )}
                        <span className="inline-flex items-center gap-1"><Users size={12} /> {row.max_uses_per_customer}/customer</span>
                        {row.expires_at && (
                          <span className="inline-flex items-center gap-1"><Calendar size={12} /> exp {new Date(row.expires_at).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => copy(row)} className="px-3 py-1.5 rounded-full border border-[#27272A] hover:border-[#00F0FF] text-xs inline-flex items-center gap-1" data-testid={`discount-copy-${row.id}`}>
                        <Copy size={12} /> Copy
                      </button>
                      <button onClick={() => toggle(row)} className={`px-3 py-1.5 rounded-full border text-xs inline-flex items-center gap-1 ${row.enabled ? 'border-[#27272A] hover:border-yellow-400 text-yellow-300' : 'border-[#27272A] hover:border-[#00F0FF] text-[#00F0FF]'}`} data-testid={`discount-toggle-${row.id}`}>
                        {row.enabled ? <><PowerOff size={12} /> Disable</> : <><Power size={12} /> Enable</>}
                      </button>
                      <button onClick={() => remove(row)} className="px-3 py-1.5 rounded-full border border-[#27272A] hover:border-red-500 text-red-400 text-xs inline-flex items-center gap-1" data-testid={`discount-delete-${row.id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDiscountUrlsPage;
